import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Library, BookOpen, PieChart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function AnalyticsTab() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/analytics/dashboard`, {
        withCredentials: true,
        headers
      });
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B] mx-auto"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8">
        <BarChart3 size={48} className="mx-auto text-[#E7E5E4] mb-3" />
        <p className="text-sm text-[#78716C]">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="analytics-tab">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <Library size={16} className="text-[#2C4C3B]" />
            <span className="text-xs tracking-wider uppercase text-[#78716C]">Total Prompts</span>
          </div>
          <p className="text-2xl font-bold text-[#1C1917]">{analytics.total_prompts}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={16} className="text-[#E27D60]" />
            <span className="text-xs tracking-wider uppercase text-[#78716C]">My Templates</span>
          </div>
          <p className="text-2xl font-bold text-[#1C1917]">{analytics.total_templates}</p>
        </div>
      </div>

      {/* Most Used Prompts */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={16} className="text-[#2C4C3B]" />
          <h3 className="text-xs tracking-wider uppercase text-[#78716C]">Most Used Prompts</h3>
        </div>
        {analytics.most_used_prompts.length === 0 ? (
          <p className="text-sm text-[#78716C] text-center py-4">No usage data yet</p>
        ) : (
          <div className="space-y-2">
            {analytics.most_used_prompts.map((prompt, idx) => (
              <div key={prompt.prompt_id} className="flex items-center justify-between p-2 bg-[#FAFAF9] rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#1C1917] line-clamp-1">{prompt.title}</p>
                  <p className="text-xs text-[#78716C]">{prompt.usage_count} uses</p>
                </div>
                <span className="text-lg font-bold text-[#2C4C3B]">#{idx + 1}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Function Distribution */}
      {Object.keys(analytics.function_distribution).length > 0 && (
        <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <PieChart size={16} className="text-[#2C4C3B]" />
            <h3 className="text-xs tracking-wider uppercase text-[#78716C]">By Function</h3>
          </div>
          <div className="space-y-2">
            {Object.entries(analytics.function_distribution)
              .sort((a, b) => b[1] - a[1])
              .map(([func, count]) => {
                const total = Object.values(analytics.function_distribution).reduce((a, b) => a + b, 0);
                const percentage = Math.round((count / total) * 100);
                return (
                  <div key={func} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1C1917] font-medium">{func}</span>
                      <span className="text-[#78716C]">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-[#F5F2EA] rounded-full h-2">
                      <div
                        className="bg-[#2C4C3B] h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Category Distribution */}
      {Object.keys(analytics.category_distribution).length > 0 && (
        <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <PieChart size={16} className="text-[#E27D60]" />
            <h3 className="text-xs tracking-wider uppercase text-[#78716C]">By Category</h3>
          </div>
          <div className="space-y-2">
            {Object.entries(analytics.category_distribution)
              .sort((a, b) => b[1] - a[1])
              .map(([category, count]) => {
                const total = Object.values(analytics.category_distribution).reduce((a, b) => a + b, 0);
                const percentage = Math.round((count / total) * 100);
                return (
                  <div key={category} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1C1917] font-medium">{category}</span>
                      <span className="text-[#78716C]">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-[#F5F2EA] rounded-full h-2">
                      <div
                        className="bg-[#E27D60] h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Most Used Templates */}
      {analytics.most_used_templates.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={16} className="text-[#E27D60]" />
            <h3 className="text-xs tracking-wider uppercase text-[#78716C]">Most Used Templates</h3>
          </div>
          <div className="space-y-2">
            {analytics.most_used_templates.map((template, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-[#FAFAF9] rounded-lg">
                <p className="text-sm font-medium text-[#1C1917] line-clamp-1">{template.template_name}</p>
                <span className="text-xs text-[#78716C]">{template.count} uses</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}